export type AdminOrgStatus = 'active' | 'suspended' | 'trial'

export interface AdminOrganization {
  orgID: string
  orgName: string
  ownerEmail: string
  ownerName: string
  status: AdminOrgStatus
  storesCount: number
  usersCount: number
  createdAt: string
  planID?: string
  planName?: string
  mrr?: number
  country?: string
  logoUrl?: string
  // Extended profile fields
  userID?: string
  salesCompanyID?: string
  orgNumber?: string
  orgAddress?: string
  orgZipCode?: string
  orgCity?: string
  orgCountry?: string
  orgPhone?: string
  orgEmail?: string
  orgWebsite?: string
  orgDescription?: string
  orgStatus?: 'Active' | 'Inactive' | 'Blacklisted'
}

export const BILLING_CYCLES = [
  'Daily',
  'Weekly',
  'Monthly',
  'Quarterly',
  'BiAnnually',
  'Annually',
] as const
export type BillingCycle = (typeof BILLING_CYCLES)[number]

export const SUBSCRIPTION_PLAN_STATUS = ['Active', 'Inactive', 'Blacklisted'] as const
export type SubscriptionPlanStatus = (typeof SUBSCRIPTION_PLAN_STATUS)[number]

export const SUBSCRIPTION_STATUS = ['Active', 'Inactive', 'Blacklisted'] as const
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[number]

export interface SubscriptionPlan {
  subscriptionPlanID: string
  name: string
  currency: string
  description: string
  billingCycle: BillingCycle
  subscriptionPlansStatus: SubscriptionPlanStatus
  createdAt?: string
  updatedAt?: string
  // View-only aggregates:
  featureCount?: number
  monthlyPrice?: number
}

export interface SubscriptionPlanFeature {
  subscriptionPlanFeatureID: string
  name: string
  unit: string
  cost: number
  isActive: boolean
  createdAt?: string
}

export interface SubscriptionPlanToFeature {
  subscriptionPlanID: string
  subscriptionPlanFeatureID: string
  subscriptionFeatureCost: number
  // View-only:
  featureName?: string
  unit?: string
}

export interface OrgSubscription {
  subscriptionToOrgID: string
  orgID: string
  orgName: string
  subscriptionPlanID: string
  planName: string
  validFrom: string
  validTo: string
  subscriptionStatus: SubscriptionStatus
  discountPerUser: number
  currency?: string
  billingCycle?: BillingCycle
  amount?: number
}

export interface SubscriptionPlanOrgDiscount {
  subscriptionPlanOrgDiscountID: string
  orgID: string
  orgName: string
  subscriptionPlanFeaturesID: string
  featureName: string
  discount: number
  discountValidFrom: string
  discountValidTo: string
  discountReason: string
}

export interface FeatureUsageLog {
  usageID: string
  orgID: string
  orgName?: string
  subscriptionPlanFeatureID: string
  featureName?: string
  usageCount: number
  usageCost: number
  usageCurrency: string
  day: string
}

export interface GlobalRole {
  globalRoleID: string
  globalRoleName: string
  description: string
  createdAt: string
  updatedAt?: string
  // View-only aggregate
  permissionCount?: number
}

export interface GlobalPermission {
  globalPermissionID: string
  globalPermissionTitle: string
  description: string
  createdAt: string
  updatedAt?: string
}

export interface GlobalRoleToPermission {
  globalRoleID: string
  globalPermissionID: string
  createdAt?: string
  updatedAt?: string
  // View-only
  globalRoleName?: string
  globalPermissionTitle?: string
}

export const USER_GLOBAL_ROLES_STATUS = ['Active', 'Inactive', 'Blacklisted'] as const
export type UserGlobalRolesStatus = (typeof USER_GLOBAL_ROLES_STATUS)[number]

export interface UserGlobalRole {
  userGlobalRoleID: string
  userID: string
  globalRoleID: string
  userGlobalRolesStatus: UserGlobalRolesStatus
  createdAt: string
  updatedAt?: string
  // View-only
  userName?: string
  userEmail?: string
  globalRoleName?: string
}

export interface AdminOverviewStats {
  totalOrganizations: number
  activeOrganizations: number
  activeSubscriptions: number
  monthlyRecurringRevenue: number
  newSignupsThisMonth: number
  currency: string
}

export const SALES_COMPANY_STATUS = ['Active', 'Inactive', 'Blacklisted'] as const
export type SalesCompanyStatus = (typeof SALES_COMPANY_STATUS)[number]

export interface SalesCompany {
  salesCompanyID: string
  orgNumber: string
  name: string
  email: string
  phoneNumber: string
  country: string
  salesCompanyStatus: SalesCompanyStatus
  createdAt?: string
  updatedAt?: string
}
